import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./AlumniRegister.css";
import "./AlumniPasswordReset.css";

const AlumniRegisterVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [email, setEmail] = useState("");
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    } else {
      navigate("/alumni-register", { replace: true });
    }
  }, [location.state, navigate]);

  const handleInputChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 1);
    setOtpValues((prevValues) => {
      const nextValues = [...prevValues];
      nextValues[index] = value;
      return nextValues;
    });

    setErrors((prev) => ({ ...prev, otp: "", form: "" }));

    if (value && index < inputs.length - 1) {
      inputs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otp = otpValues.join("");
    if (otp.length !== 4) {
      setErrors((prev) => ({ ...prev, otp: "Enter a valid 4-digit verification code" }));
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        "/backend/alumni/register/verify-otp",
        { email, otp },
        { withCredentials: true }
      );

      if (response.data.success) {
        navigate("/alumni-login", {
          replace: true,
          state: { message: "Registration successful! Please login with your email and password." },
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          form: response.data.message || "Invalid verification code",
        }));
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to verify code. Please try again.";
      setErrors((prev) => ({
        ...prev,
        form: message,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResendLoading(true);
      setResendMessage("");
      setErrors((prev) => ({ ...prev, form: "" }));

      const response = await axios.post(
        "/backend/alumni/register/resend-otp",
        { email },
        { withCredentials: true }
      );

      if (response.data.success) {
        setResendMessage("A new verification code has been sent to your email.");
        setOtpValues(["", "", "", ""]);
        inputs[0].current?.focus();
      } else {
        setErrors((prev) => ({
          ...prev,
          form: response.data.message || "Failed to resend code",
        }));
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to resend code. Please try again.";
      setErrors((prev) => ({
        ...prev,
        form: message,
      }));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="alumni-login-page alumni-login-shell">
      <div className="alumni-login-card alumni-register-card">
        <p className="alumni-page-eyebrow">PJC Alumni Fund</p>
        <h1 className="alumni-login-title">Verify Email</h1>
        <div className="alumni-login-line"></div>

        <p className="alumni-reset-helper">
          A verification code has been sent to <strong>{email}</strong>. Enter the 4-digit code below to complete your registration.
        </p>

        {resendMessage && <div className="alumni-form-banner success">{resendMessage}</div>}
        {errors.form && <div className="alumni-form-banner error">{errors.form}</div>}

        <form onSubmit={handleSubmit}>
          <div className="alumni-otp-row">
            {inputs.map((input, index) => (
              <input
                ref={input}
                key={index}
                className="alumni-otp-input"
                inputMode="numeric"
                maxLength="1"
                value={otpValues[index]}
                onChange={(e) => handleInputChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                aria-invalid={Boolean(errors.otp)}
              />
            ))}
          </div>
          {errors.otp && <p className="alumni-field-error alumni-otp-error">{errors.otp}</p>}

          <button type="submit" className="alumni-login-btn" disabled={loading}>
            {loading ? "Verifying..." : "Verify & Register"}
          </button>

          <div className="alumni-register-text">
            Didn't receive the code?{" "}
            <button
              type="button"
              className="alumni-resend-link"
              onClick={handleResend}
              disabled={resendLoading}
            >
              {resendLoading ? "Sending..." : "Resend code"}
            </button>
          </div>

          <div className="alumni-register-text">
            <Link to="/alumni-register">Back to registration</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlumniRegisterVerify;
